/**
 * Opus DTX (Discontinuous Transmission) silence detector.
 *
 * Opus VAD/DTX stops sending voice frames during silence and emits SID frames
 * (comfort noise) every ~400-1000ms. To the receiver this looks like:
 *   - very low pps (5-11 vs nominal 50)
 *   - zero packet loss (everything sent arrived)
 *   - very low bitrate (1-2 kbps vs ~24 kbps for voice)
 *   - high "jitter" — but this is just the SID frame interval, not real network jitter
 *
 * Without this guard, the worst-of-3 quality formula sees jitter > 60ms and
 * brands the call Poor. Validated against three production false-positive
 * calls (Apr 2026): 3/3 trigger this guard, 0 true-Poor calls do.
 *
 * Opus-only assumption — other codecs may not use DTX or use different SID
 * patterns. Re-evaluate thresholds if codec set ever expands.
 */
export function isDtxTick(args: {
  pps: number
  lossDelta: number
  bitrateKbps: number
}): boolean {
  const { pps, lossDelta, bitrateKbps } = args
  // Defensive: negative deltas mean baseline math broke (e.g. unhandled
  // counter reset). Don't silently call that "silence".
  if (pps < 0 || bitrateKbps < 0) return false
  return pps < 25 && lossDelta === 0 && bitrateKbps < 5
}
